import type {
  CentinelaStatusCounts,
  CentinelaUnit,
} from "../../../features/centinela/types";
import type {
  ChipTone,
  FleetStatusBreakdown,
  UnitChip,
  UnitWatchState,
  WatchStatus,
} from "../types";
import { WATCH_STATUSES } from "../types";

const CHIP_TONE_BY_API_TYPE: Record<string, ChipTone> = {
  green: "success",
  teal: "success",
  success: "success",
  blue: "info",
  info: "info",
  amber: "warning",
  yellow: "warning",
  warning: "warning",
  red: "error",
  error: "error",
  gray: "neutral",
  grey: "neutral",
  neutral: "neutral",
};

const toChipTone = (type: string): ChipTone =>
  CHIP_TONE_BY_API_TYPE[type?.toLowerCase()] ?? "neutral";

const toChips = (unit: CentinelaUnit): UnitChip[] =>
  (unit.chips ?? []).map((chip) => ({
    label: chip.label,
    tone: toChipTone(chip.type),
  }));

const isWatchStatus = (value: string): value is WatchStatus =>
  (WATCH_STATUSES as readonly string[]).includes(value);

export const UNKNOWN_WATCH_STATUS: WatchStatus = "sin_custodia";

export const toWatchStatus = (status: string | null | undefined): WatchStatus =>
  status && isWatchStatus(status) ? status : UNKNOWN_WATCH_STATUS;

export interface UnitTelemetryOverride {
  speedKph?: number | null;
  headingDeg?: number | null;
  driverPhoto?: string | null;
  fuelPercent?: number | null;
  position?: { lat: number; lng: number } | null;
  updatedAt?: string | null;
  address?: string | null;
}

export function mapCentinelaUnit(
  unit: CentinelaUnit,
  telemetry?: UnitTelemetryOverride,
): UnitWatchState {
  const status = toWatchStatus(unit.status);
  const reported =
    unit.position?.lat != null && unit.position?.lng != null
      ? { lat: unit.position.lat, lng: unit.position.lng }
      : null;
  const position = telemetry?.position ?? reported;

  return {
    unitId: unit.device_id,
    deviceId: unit.device_id,
    status,
    hasOperatorPhone: unit.has_operator_phone === true,
    underCustody: unit.under_custody === true,
    openCaseId: unit.open_case_id ?? null,
    isCalling: false,
    address: telemetry?.address ?? null,
    aiMessageType: unit.ai_message_type ?? "info",
    speedKph: telemetry?.speedKph ?? unit.position?.speed_kph ?? null,
    headingDeg: telemetry?.headingDeg ?? null,
    driverPhoto: telemetry?.driverPhoto ?? null,
    fuelPercent: telemetry?.fuelPercent ?? 0,
    updatedAt: telemetry?.updatedAt ?? unit.last_position_at ?? null,
    position,
    aiMessage: unit.ai_message ?? "",
    chips: toChips(unit),
  };
}

export function emptyWatchState(unitId: string): UnitWatchState {
  return {
    unitId,
    deviceId: null,
    status: UNKNOWN_WATCH_STATUS,
    hasOperatorPhone: false,
    underCustody: false,
    openCaseId: null,
    isCalling: false,
    address: null,
    aiMessageType: "info",
    speedKph: null,
    headingDeg: null,
    driverPhoto: null,
    fuelPercent: 0,
    updatedAt: null,
    position: null,
    aiMessage: "",
    chips: [],
  };
}

export function mapCentinelaBreakdown(
  counts: CentinelaStatusCounts | undefined,
): FleetStatusBreakdown {
  return WATCH_STATUSES.reduce<FleetStatusBreakdown>(
    (acc, status) => ({ ...acc, [status]: counts?.[status] ?? 0 }),
    {} as FleetStatusBreakdown,
  );
}

export function buildBreakdownFromUnits(units: CentinelaUnit[]): FleetStatusBreakdown {
  return units.reduce<FleetStatusBreakdown>(
    (acc, unit) => {
      const status = toWatchStatus(unit.status);
      return { ...acc, [status]: acc[status] + 1 };
    },
    mapCentinelaBreakdown(undefined),
  );
}
